import { ReactElement, useContext, useEffect, useRef, useState, useMemo } from 'react'
import ReactHtmlParser from 'react-html-parser'

import Calendar from 'react-calendar'
import { getDaysInMonth } from 'date-fns'

import Popup from 'reactjs-popup'

import styles from './CalendarTicketPopup.module.scss'

import type { ProductType, TicketAvailability } from '../../../../types/ProductType'
import { DataLayerAPI } from '../../../../api/DataLayerAPI';
import { CartContext } from '../../../../contexts/CartContext';
import { format } from "date-fns-tz";

type CalendarView = 'month' | 'year' | 'decade' | 'century';

type CalendarTicketPopupProps = {
  popupOpen: boolean,
  onClose: (event?: React.SyntheticEvent | KeyboardEvent | TouchEvent | MouseEvent) => void,
  product: ProductType,
  qtyPicker?: JSX.Element | null,
  childQtyPicker?: JSX.Element | null,
  adultQtyPicker?: JSX.Element | null,
  addToBasketBtn: (dateId: string, callback: () => void) => ReactElement,
  setPopupOpen?: React.Dispatch<React.SetStateAction<boolean>>,
  setDateAdultPrice?: Function,
  setDateChildPrice?: Function,
  isDatedTicket ?: boolean | any,
  year?: string,
}
const dataLayerAPI = new DataLayerAPI()

async function fetchMonthAvailability(productId: string, date: Date) {
  const today = new Date();
  // Use today's date if the provided date is in the past
  const effectiveDate = date < today ? today : date;

  const dateFrom = format(effectiveDate, 'yyyy-MM-dd', { timeZone: 'UTC' });
  const dateTo = format(effectiveDate, 'yyyy-MM-', { timeZone: 'UTC' }) + getDaysInMonth(effectiveDate);
  const url = `${process.env.NEXT_PUBLIC_API_BASE_URL}/products/${productId}?date_from=${dateFrom}&date_to=${dateTo}`;

  try {
    const result = await fetch(url);
    if (result.status === 200) {
      return await result.json();
    }
  } catch (error) {
    console.error("Failed to fetch availability:", error);
  }
  return null;
}

function getAvailabilityForDate(availability: TicketAvailability[], date: Date) {
  if (!availability) {
    return undefined;
  }
  return availability.find(a => a.date === format(date, 'yyyy-MM-dd'));
}

export function CalendarTicketPopup({ popupOpen, setPopupOpen, onClose, product, qtyPicker, childQtyPicker, adultQtyPicker, addToBasketBtn, setDateAdultPrice, setDateChildPrice, isDatedTicket, year }: CalendarTicketPopupProps) {

  let hotelCheckInDate:string | undefined = undefined;

  const {cart} = useContext(CartContext)
  const [selectedDatePrice, setSelectedDatePrice] = useState(0)
  const [selectedDateAdultPrice, setSelectedDateAdultPrice] = useState(0)
  const [selectedDateChildPrice, setSelectedDateChildPrice] = useState(0)
  const [availability, setAvailability] : [ availability: TicketAvailability[], setAvailability: Function ] = useState([])
  const [adultAvailability, setAdultAvailability] : [ adultAvailability: TicketAvailability[], setAdultAvailability: Function ] = useState([])
  const [childAvailability, setChildAvailability] : [ childAvailability: TicketAvailability[], setChildAvailability: Function ] = useState([])
  const [loadingAvailability, setLoadingAvailability] = useState(true)

  const cartTickets = cart.data.tickets;
  if (cartTickets.length > 0) {
    cartTickets.map((ticket) => {
      if (ticket.tags.includes('rooms')) {
        hotelCheckInDate = ticket.attributes['5'].join('');
      }
    });
  }

  const initialDate = useMemo(() => {
    if (hotelCheckInDate) {
      return new Date(hotelCheckInDate);
    }
    const firstAvailableYear = product.calendar_first_available_month
      ? new Date(product.calendar_first_available_month).getFullYear().toString()
      : '';
    if (year && firstAvailableYear && year > firstAvailableYear) {
      return new Date(`${year}-01-01T12:00:00`);
    }
    return product.calendar_first_available_month
      ? new Date(product.calendar_first_available_month)
      : new Date();
  }, [hotelCheckInDate, product.calendar_first_available_month, year]);

  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [ renderPopup, setRenderPopup ] = useState(false)
  useEffect(() => setRenderPopup(true), [ setRenderPopup ])
  const [showDetails, setShowDetails] = useState(false);

  // Search for the first available date when the popup opens
  useEffect(() => {
    if (!popupOpen) return;

    let isCancelled = false;
    setLoadingAvailability(true);

    const findFirstAvailableDate = async (searchDate: Date) => {
      if (isCancelled || searchDate.getFullYear().toString() > initialDate.getFullYear().toString()) {
        setLoadingAvailability(false);
        return;
      }

      const productResult = await fetchMonthAvailability(product.id, searchDate);
      if (isCancelled || !productResult) {
        setLoadingAvailability(false);
        return;
      }

      const currentAdultAvail = productResult.tickets?.find((t:any) => t.type === "Adult")?.availability || [];
      const currentAvail = isDatedTicket ? currentAdultAvail : productResult.tickets?.[0]?.availability || [];

      if (currentAvail.length > 0) {
        const firstDay = currentAvail.find((d: TicketAvailability) => new Date(d.date + 'T00:00:00') >= new Date(new Date().setHours(0, 0, 0, 0)));
        if (firstDay && !hotelCheckInDate) {
          setSelectedDate(new Date(firstDay.date + 'T12:00:00'));
        }
        setAdultAvailability(currentAdultAvail);
        setChildAvailability(productResult.tickets?.find((t:any) => t.type === "Child")?.availability || []);
        setAvailability(productResult.tickets?.[0]?.availability || []);
        setLoadingAvailability(false);
      } else {
        const nextMonth = new Date(searchDate);
        nextMonth.setMonth(searchDate.getMonth() + 1, 1);
        await findFirstAvailableDate(nextMonth);
      }
    };

    findFirstAvailableDate(initialDate);

    return () => { isCancelled = true; };
  }, [product.id, popupOpen, isDatedTicket, initialDate, hotelCheckInDate]);


  // Handle when a user manually changes the month in the calendar
  const onChangeMonth = async ({ activeStartDate, view }: { activeStartDate: Date | null, view: CalendarView }) => {
    if (!activeStartDate) return;
    setLoadingAvailability(true);
    const productResult = await fetchMonthAvailability(product.id, activeStartDate);
    if (productResult) {
      setAdultAvailability(productResult.tickets?.find((t:any) => t.type === "Adult")?.availability || []);
      setChildAvailability(productResult.tickets?.find((t:any) => t.type === "Child")?.availability || []);
      setAvailability(productResult.tickets?.[0]?.availability || []);
    }
    setLoadingAvailability(false);
  }

  const handleToggleClick = (e: { preventDefault: () => void; }) => {
    e.preventDefault();
    setShowDetails(!showDetails);
  };

  useEffect(() => {
    if (!isDatedTicket) {
      const avail = getAvailabilityForDate(availability, selectedDate)
      avail && setSelectedDatePrice(avail.price)
    } else {
      const adultAvail = getAvailabilityForDate(adultAvailability, selectedDate)
      const childAvail = getAvailabilityForDate(childAvailability, selectedDate)
      adultAvail && setSelectedDateAdultPrice(adultAvail.price)
      setDateAdultPrice && adultAvail && setDateAdultPrice(adultAvail.price)
      childAvail && setSelectedDateChildPrice(childAvail.price)
      setDateChildPrice && childAvail && setDateChildPrice(childAvail.price)
    }
  }, [selectedDate, availability, adultAvailability, isDatedTicket, childAvailability, setDateAdultPrice, setDateChildPrice])

  const calendarDiv = useRef<HTMLInputElement>(null)

  const getTileContent = ({ date, view }: { date: Date; view: CalendarView }): JSX.Element => {
    const avail = getAvailabilityForDate((availability.length !== 0 ? availability : adultAvailability), date)
    return avail ? <div className={ styles.price }>£{ avail.price }</div> : <></>
  }

  const getTileDisabled = ({ date, view }: { date: Date; view: CalendarView }): boolean => {
    const avail = getAvailabilityForDate(availability.length !== 0 ? availability : adultAvailability, date)
    return avail ? false : true
  }

  let selectedDateId: string | undefined = undefined;
  if (availability || (adultAvailability && selectedDate)) {
    selectedDateId = getAvailabilityForDate(availability.length !== 0 ? availability : adultAvailability, selectedDate)?.date_id
  }

  return renderPopup ? (
    <Popup open={popupOpen} onClose={() => {
      onClose
      setPopupOpen ? setPopupOpen(false) : null
      dataLayerAPI.logModalAction('Modal Closed','Express Pass Calendar',product.title)
      }} modal lockScroll onOpen={() => dataLayerAPI.logModalAction('Modal Opened','Express Pass Calendar',product.title)}>
      {(close: Function) => (
        <div className={ `${styles.modal} ${loadingAvailability ? styles.loading : ''}` }>
          <button className={styles.close} onClick={ () => close() }>
            <span className='visuallyHidden'>Close</span>
          </button>
          <div className={styles.popupHeader}>
            <h3>{ product.title }</h3>
          </div>
          <div className={ `popup-body-calendar ${styles.popupBody}` }>
            <div>
              <h4>Description</h4>
              { ReactHtmlParser(product.desc) }
              <h4>Ticket includes</h4>
              { ReactHtmlParser(product.ticket_includes) }
              <h4>
                <a className={`${styles.toggle} ${showDetails ? styles.open : ''}`} href="#na" onClick={handleToggleClick}>Conditions</a>
              </h4>
              {showDetails === true && ReactHtmlParser(product.conditions) }
            </div>
            <div className={ styles.calendar }>
              <Calendar
                onChange={(value) => {
                  if (value instanceof Date) {
                    setSelectedDate(value);
                  }
                }}
                value={ selectedDate }
                onActiveStartDateChange={ onChangeMonth }
                minDate={ new Date() }
                inputRef={ calendarDiv }
                tileContent={ getTileContent }
                tileDisabled={ getTileDisabled }
                minDetail='month'
                maxDetail='month'
              />
              <div className={ styles.selectedDate }>
                Selected date: { format(selectedDate, 'dd/MM/yyyy') }
              </div>
              <div className={styles.pricesSection}>
              {!isDatedTicket &&
              <><div className={styles.qtySection}>
                    {qtyPicker}
                  </div><div className={styles.selectedDatePrice}>
                      £{selectedDatePrice}
                    </div></>}
              {isDatedTicket && <>
                <div className={styles.qtySection}>
                  {adultQtyPicker}
                </div>
                  <div className={styles.qtySection}>
                    {childQtyPicker}
                  </div>
                </>}
                </div>
            </div>
          </div>
          <div className={ `popup-footer ${styles.popupFooter}` }>
            <button
              className={styles.btnAlt}
              onClick={ () => close() }
            >CANCEL</button>
            { selectedDateId ? addToBasketBtn(selectedDateId, () => close()) : null }
          </div>
        </div>
      )}
    </Popup>
  ) : null
}
